/**
 * <xp-product-gallery> — main image + thumbnail rail for the product page.
 *
 * The main track is a native horizontal scroll-snap strip (swipe works without JS).
 * This component wires thumbnails, prev/next buttons and the "1 / N" counter to it.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontal scroll-snap container
 *   [data-gallery-slide]               — each slide inside the track
 *   [data-gallery-thumbs]              — scrollable thumbnail rail
 *   [data-gallery-thumb][data-index]   — thumbnail buttons
 *   [data-gallery-prev], [data-gallery-next] — optional arrow buttons
 *   [data-gallery-current]             — optional current index output
 */
class XpProductGallery extends HTMLElement {
  #ac;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.rail = this.querySelector('[data-gallery-thumbs]');
    this.current = this.querySelector('[data-gallery-current]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.index)), opts);
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.#index + 1), opts);
    this.track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });
    this.track.addEventListener('keydown', this.#onKey, opts);

    this.#sync(0);

    // Variant -> image: jump to the selected variant's image on load and on every colour/size change.
    try {
      this.variantMedia = JSON.parse(this.querySelector('[data-variant-media]')?.textContent || '{}');
    } catch {
      this.variantMedia = {};
    }
    const initial = this.variantMedia[this.dataset.initialVariant];
    if (initial !== undefined) this.go(initial, true);
    document.addEventListener('change', this.#onVariantChange, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  go(index, instant = false) {
    const count = this.slides.length;
    if (!count) return;
    const next = (index + count) % count;
    const slide = this.slides[next];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const left = isRtl ? -(this.track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    this.track.scrollTo({ left, behavior: reduce || instant ? 'auto' : 'smooth' });
    this.#sync(next);
  }

  #onVariantChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('s-variant-picker')) return;
    // s-variant-picker syncs the hidden id input in its own change handler; read it on the next tick.
    setTimeout(() => {
      const id = document.querySelector('[data-product-form] input[name="id"]')?.value;
      const index = this.variantMedia?.[id];
      if (index !== undefined && index !== this.#index) this.go(index);
    }, 0);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (index !== this.#index) this.#sync(Math.min(index, this.slides.length - 1));
    });
  };

  #onKey = (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.#index + 1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.#index - 1); }
  };

  #sync(index) {
    this.#index = index;
    if (this.current) this.current.textContent = String(index + 1);
    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.toggleAttribute('data-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    const thumb = this.thumbs[index];
    if (thumb && this.rail) {
      const vertical = this.rail.scrollHeight > this.rail.clientHeight + 1;
      if (vertical) {
        const top = thumb.offsetTop - (this.rail.clientHeight - thumb.offsetHeight) / 2;
        this.rail.scrollTo({ top, behavior: 'smooth' });
      } else {
        const left = thumb.offsetLeft - (this.rail.clientWidth - thumb.offsetWidth) / 2;
        this.rail.scrollTo({ left, behavior: 'smooth' });
      }
    }
  }
}

if (!customElements.get('xp-product-gallery')) {
  customElements.define('xp-product-gallery', XpProductGallery);
}
